/**
 * Data Room document requests: the document, deleted-document and revision
 * shapes as the server sends them, the room listing, and every document
 * read and mutation — detail, history, revisions, creation, move, edit,
 * restore, soft deletion and recovery.
 */

import type { DataRoomFolder } from './data-room-folders.js';
import { request } from './request.js';

export interface DataRoomParticipant {
  readonly id: string;
  readonly displayName: string;
}

export interface DataRoomDocumentSummary {
  readonly id: string;
  readonly slug: string;
  readonly title: string;
  readonly category: string;
  /**
   * Who the server says this document is for. Present only on builds whose
   * server sends it, which is why it is optional here and why nothing in
   * this bundle interprets the value — the strings that name the audiences
   * live in the build that supplies the badge.
   */
  readonly audience?: string;
  /** The containing user folder, or null for the category root. */
  readonly folderId: string | null;
  readonly version: number;
  readonly createdBy: DataRoomParticipant;
  readonly updatedBy: DataRoomParticipant;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface DataRoomDocumentDetail extends DataRoomDocumentSummary {
  readonly content: string;
  /** Set while the document is soft-deleted; null for a live document. */
  readonly deletedAt: string | null;
  readonly deletedBy: DataRoomParticipant | null;
}

/** One soft-deleted document, as the deleted-documents view lists it. */
export interface DataRoomDeletedDocumentSummary extends DataRoomDocumentSummary {
  readonly deletedAt: string;
  readonly deletedBy: DataRoomParticipant;
}

export interface DataRoomRevisionMeta {
  readonly version: number;
  readonly changeNote: string | null;
  readonly restoredFromVersion: number | null;
  readonly createdBy: DataRoomParticipant;
  readonly createdAt: string;
}

export interface DataRoomRevisionDetail extends DataRoomRevisionMeta {
  readonly content: string;
}

export function fetchDataRoom(): Promise<{
  folders: readonly DataRoomFolder[];
  documents: readonly DataRoomDocumentSummary[];
  deletedDocuments: readonly DataRoomDeletedDocumentSummary[];
}> {
  return request('GET', '/workspace/data-room');
}

export function fetchDataRoomDocument(documentId: string): Promise<{
  document: DataRoomDocumentDetail;
  folderPath: readonly { id: string; name: string }[];
}> {
  return request(
    'GET',
    `/workspace/data-room/documents/${encodeURIComponent(documentId)}`,
  );
}

export function fetchDataRoomHistory(
  documentId: string,
): Promise<{ revisions: readonly DataRoomRevisionMeta[] }> {
  return request(
    'GET',
    `/workspace/data-room/documents/${encodeURIComponent(documentId)}/revisions`,
  );
}

export function fetchDataRoomRevision(
  documentId: string,
  version: number,
): Promise<{ revision: DataRoomRevisionDetail }> {
  return request(
    'GET',
    `/workspace/data-room/documents/${encodeURIComponent(documentId)}/revisions/${String(version)}`,
  );
}

export function createDataRoomDocument(
  idempotencyKey: string,
  document: {
    title: string;
    category?: string;
    folderId?: string;
    content: string;
    changeNote?: string;
    audience?: string;
  },
): Promise<{ documentId: string; slug: string; version: number }> {
  return request('POST', '/workspace/data-room/documents', {
    body: document,
    idempotencyKey,
  });
}

export function moveDataRoomDocument(
  idempotencyKey: string,
  documentId: string,
  body: { expectedVersion: number; category?: string; folderId?: string },
): Promise<{ documentId: string; changed: boolean }> {
  return request(
    'POST',
    `/workspace/data-room/documents/${encodeURIComponent(documentId)}/move`,
    { body, idempotencyKey },
  );
}

export function editDataRoomDocument(
  idempotencyKey: string,
  documentId: string,
  body: {
    expectedVersion: number;
    title?: string;
    content: string;
    changeNote?: string;
  },
): Promise<{ documentId: string; version: number; changed: boolean }> {
  return request(
    'POST',
    `/workspace/data-room/documents/${encodeURIComponent(documentId)}/edit`,
    { body, idempotencyKey },
  );
}

export function restoreDataRoomRevision(
  idempotencyKey: string,
  documentId: string,
  body: { expectedVersion: number; version: number },
): Promise<{ documentId: string; version: number; changed: boolean }> {
  return request(
    'POST',
    `/workspace/data-room/documents/${encodeURIComponent(documentId)}/restore`,
    { body, idempotencyKey },
  );
}

/** Soft-delete: the document leaves every ordinary surface, restorably. */
export function deleteDataRoomDocument(
  idempotencyKey: string,
  documentId: string,
  body: { expectedVersion: number },
): Promise<{ documentId: string; deleted: boolean }> {
  return request(
    'POST',
    `/workspace/data-room/documents/${encodeURIComponent(documentId)}/delete`,
    { body, idempotencyKey },
  );
}

/** Undo a soft deletion. `restore` already means revisions; this recovers. */
export function recoverDataRoomDocument(
  idempotencyKey: string,
  documentId: string,
  body: { expectedVersion: number },
): Promise<{ documentId: string; deleted: boolean }> {
  return request(
    'POST',
    `/workspace/data-room/documents/${encodeURIComponent(documentId)}/recover`,
    { body, idempotencyKey },
  );
}
